import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {Phone, PhoneOff, Users, Video, X} from 'lucide-react';
import type {RoomCallSummary} from '@phonehq/rtc-core';
import {useCallBlock, useRoomCallSummary} from '../../groupCalls/hooks/useGroupCall';
import {openRoomCallLobby} from '../../groupCalls/components/groupCallActions';
import {countCallPeople} from '../../groupCalls/services/callPeople';
import type {RoomCallEntry} from '../../groupCalls/services/roomCallEntry';
import {useElapsed} from '../hooks/useElapsed';
import {CallBlockCard} from './CallBlockCard';
import {useCallPeopleText} from './useCallPeopleText';
import {callsMessages as m} from './callsMessages';

const MINUTE_MS = 60_000;

interface EndedCall {
    readonly roomId: string;
    readonly lastedMs: number;
}

const useEndedCall = (roomId: string, summary: RoomCallSummary | undefined): [EndedCall | undefined, () => void] => {
    const [ended, setEnded] = useState<EndedCall>();
    const seen = useRef<{roomId: string; startedAt?: number}>();

    useEffect(() => {
        setEnded(undefined);
        seen.current = undefined;
    }, [roomId]);

    useEffect(() => {
        if (summary && summary.participantCount > 0) {
            const known = seen.current?.roomId === roomId ? seen.current.startedAt : undefined;
            const startedAt = Math.min(summary.startedAt ?? Date.now(), known ?? Number.POSITIVE_INFINITY);
            seen.current = {roomId, startedAt};
            setEnded(undefined);
            return;
        }
        const previous = seen.current;
        if (previous && previous.roomId === roomId) {
            setEnded({roomId, lastedMs: Date.now() - (previous.startedAt ?? Date.now())});
            seen.current = undefined;
        }
    }, [roomId, summary]);

    return [ended, () => setEnded(undefined)];
};

const BannerFrame = ({tone, label, children}: {tone: 'live' | 'ended'; label: string; children: React.ReactNode}) => (
    <div
        role="status"
        aria-label={label}
        data-testid="chat-call-banner"
        data-tone={tone}
        className={`phq-call-light flex h-12 shrink-0 items-center gap-3 border-b border-solid border-[var(--phq-call-line)] pl-4 text-sm ${tone === 'live' ? 'bg-[var(--phq-call-brand-tint-subtle)] pr-4' : 'bg-[var(--phq-call-surface-muted)] pr-2'}`}
    >
        {children}
    </div>
);

const BannerGlyph = ({tone, children}: {tone: 'live' | 'ended'; children: React.ReactNode}) => (
    <span className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${tone === 'live' ? 'bg-[var(--phq-call-brand-tint)] text-[var(--phq-call-brand-ink)]' : 'bg-[var(--phq-call-line)] text-[var(--phq-call-ink-muted)]'}`}>
        {children}
    </span>
);

const LiveBanner = ({roomId, summary, entry}: {roomId: string; summary: RoomCallSummary; entry: RoomCallEntry}) => {
    const intl = useIntl();
    const people = useCallPeopleText(roomId, summary.userIds, 3);
    const timer = useElapsed(summary.startedAt);
    const count = countCallPeople(summary.userIds);
    const full = entry.kind === 'group_full';
    const busy = entry.kind === 'group_join' && entry.busy;
    const intent = entry.kind === 'group_join' ? entry.intent : (summary.intent ?? 'audio');
    const Icon = intent === 'video' ? Video : Phone;
    const joinLabel = intl.formatMessage(m.join);
    return (
        <BannerFrame tone="live" label={intl.formatMessage(m.bannerRegion)}>
            <BannerGlyph tone="live"><Users size={16} aria-hidden="true"/></BannerGlyph>
            <span className="min-w-0 flex-1 truncate">
                <span className="font-semibold text-[var(--phq-call-ink)]">{intl.formatMessage(full ? m.bannerFull : m.bannerInProgress)}</span>
                <span className="text-[var(--phq-call-ink-muted)]">
                    {full
                        ? ` · ${intl.formatMessage(m.bannerFullDetail, {count, max: entry.kind === 'group_full' ? entry.maxParticipants : count})}`
                        : `${people ? ` · ${people}` : ''} · ${intl.formatMessage(m.inCall, {count})}${timer ? ' · ' : ''}`}
                </span>
                {!full && timer && <span className="tabular-nums text-[var(--phq-call-ink-muted)]">{timer}</span>}
            </span>
            {full ? (
                <button
                    type="button"
                    disabled
                    aria-disabled="true"
                    data-testid="group-call-full"
                    className="inline-flex h-8 shrink-0 cursor-not-allowed items-center gap-2 whitespace-nowrap rounded-[10px] bg-[var(--phq-call-line)] px-3.5 text-sm font-semibold text-[var(--phq-call-ink-muted)]"
                >
                    <Icon size={16} aria-hidden="true"/>
                    {joinLabel}
                </button>
            ) : (
                <button
                    type="button"
                    data-testid="group-call-join"
                    aria-label={busy ? `${joinLabel}, ${intl.formatMessage(m.joinBusy)}` : undefined}
                    aria-disabled={busy || undefined}
                    title={busy ? intl.formatMessage(m.joinBusy) : undefined}
                    onClick={() => openRoomCallLobby(roomId, intent, 'chat')}
                    className={`phq-call-press inline-flex h-8 shrink-0 items-center gap-2 whitespace-nowrap rounded-[10px] bg-[var(--phq-call-brand)] px-3.5 text-sm font-semibold text-white ${busy ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                    <Icon size={16} aria-hidden="true"/>
                    {joinLabel}
                </button>
            )}
        </BannerFrame>
    );
};

const EndedBanner = ({lastedMs, onDismiss}: {lastedMs: number; onDismiss: () => void}) => {
    const intl = useIntl();
    return (
        <BannerFrame tone="ended" label={intl.formatMessage(m.bannerEndedRegion)}>
            <BannerGlyph tone="ended"><PhoneOff size={16} aria-hidden="true"/></BannerGlyph>
            <span className="min-w-0 flex-1 truncate">
                <span className="font-semibold text-[var(--phq-call-ink)]">{intl.formatMessage(m.bannerEnded)}</span>
                <span className="text-[var(--phq-call-ink-muted)]"> · {intl.formatMessage(m.bannerLasted, {minutes: Math.floor(lastedMs / MINUTE_MS)})}</span>
            </span>
            <button
                type="button"
                aria-label={intl.formatMessage(m.dismiss)}
                data-testid="chat-call-banner-dismiss"
                onClick={onDismiss}
                className="phq-call-press inline-flex h-9 w-9 items-center justify-center rounded-[10px] text-[var(--phq-call-ink-muted)] hover:bg-[var(--phq-call-line)]"
            >
                <X size={18} aria-hidden="true"/>
            </button>
        </BannerFrame>
    );
};

interface ChatCallBannerProps {
    readonly roomId: string;
    readonly entry: RoomCallEntry;
    readonly onHeight?: (height: number) => void;
}

export const ChatCallBanner = ({roomId, entry, onHeight}: ChatCallBannerProps) => {
    const summary = useRoomCallSummary(roomId);
    const block = useCallBlock();
    const [ended, dismissEnded] = useEndedCall(roomId, summary);
    const ref = useRef<HTMLDivElement>(null);
    const live = !!summary && summary.participantCount > 0 && (entry.kind === 'group_join' || entry.kind === 'group_full');
    const showBlock = block?.origin === 'chat' && block.roomId === roomId;
    const showEnded = !live && !!ended && entry.kind !== 'one_to_one' && entry.kind !== 'hidden';

    useLayoutEffect(() => {
        const node = ref.current;
        if (!onHeight) {
            return;
        }
        if (!node) {
            onHeight(0);
            return;
        }
        onHeight(node.getBoundingClientRect().height);
        const observer = new ResizeObserver(() => onHeight(node.getBoundingClientRect().height));
        observer.observe(node);
        return () => {
            observer.disconnect();
            onHeight(0);
        };
    }, [onHeight, live, showBlock, showEnded]);

    if (!live && !showEnded && !showBlock) {
        return null;
    }
    return (
        <div ref={ref} className="flex shrink-0 flex-col">
            {live && summary && <LiveBanner roomId={roomId} summary={summary} entry={entry}/>}
            {showEnded && ended && <EndedBanner lastedMs={ended.lastedMs} onDismiss={dismissEnded}/>}
            {showBlock && block && <CallBlockCard block={block} className="mx-4 mt-3"/>}
        </div>
    );
};
